/**
 * Значок статуса собственного сообщения.
 *
 * CC §5.3: восемь состояний `local_state`. Отображение:
 *   queued / pending_media — часы «ожидает»;
 *   sending                — часы (неустойчивое состояние);
 *   failed                 — восклицательный знак, действие «Повторить»;
 *   sent                   — одна галочка;
 *   delivered              — две галочки;
 *   read                   — две галочки акцентным цветом;
 *   tombstone              — значка нет.
 *
 * ПС §11: «сообщение не должно выглядеть доставленным», если оно не отправлено.
 * Один и тот же набор значков и цветов в пузыре (`onBubble`) и в строке
 * списка чатов: «прочитано» везде акцентное, «не отправлено» — красное.
 */
import type { Conversation } from '@shared/api/types'
import type { LocalMessageState } from '@shared/db'
import { Icon } from '@shared/ui'

import styles from './message.module.css'

interface MessageReceiptProps {
  state?: LocalMessageState
  /** Для строки списка: показать статус последнего собственного сообщения. */
  conversation?: Conversation
  /** Светлый вариант — поверх акцентного пузыря. */
  onBubble?: boolean
}

export function MessageReceipt({ state, conversation, onBubble = false }: MessageReceiptProps) {
  const resolved = state ?? (conversation ? lastOwnState(conversation) : null)
  if (!resolved || resolved === 'tombstone') return null

  const className = onBubble ? styles[`receipt_${resolved}`] : styles['receiptMuted']

  switch (resolved) {
    case 'queued':
    case 'pending_media':
    case 'sending':
      return (
        <span className={className} title="Ожидает отправки" aria-label="Ожидает отправки">
          <Icon name="clock" size={14} />
        </span>
      )

    case 'failed':
      return (
        <span className={styles['receipt_failed']} title="Не отправлено" aria-label="Не отправлено">
          <Icon name="alert" size={14} />
        </span>
      )

    case 'sent':
      return (
        <span className={className} title="Отправлено" aria-label="Отправлено">
          <Icon name="check" size={14} />
        </span>
      )

    case 'delivered':
      return (
        <span className={className} title="Доставлено" aria-label="Доставлено">
          <Icon name="check-double" size={14} />
        </span>
      )

    case 'read':
      return (
        <span className={styles['receipt_read']} title="Прочитано" aria-label="Прочитано">
          <Icon name="check-double" size={14} />
        </span>
      )

    default:
      return null
  }
}

/**
 * Статус последнего собственного сообщения диалога — для строки списка.
 * Вычисляется из курсоров контрагента (CC §5.3 правило 1), а не хранится.
 */
function lastOwnState(conversation: Conversation): LocalMessageState | null {
  const last = conversation.last_message
  if (!last) return null

  // Последнее сообщение не наше — статуса не показываем. У поста в канал получателей много,
  // прочтение никто не подтверждает — галочек нет вовсе (изменение v1.7).
  if (!conversation.counterpart || last.sender_id === conversation.counterpart.id) return null
  if (last.deleted) return null

  const receipts = conversation.counterpart_receipts
  const readSeq = receipts?.last_read_seq ?? null
  const deliveredSeq = receipts?.last_delivered_seq ?? 0

  if (readSeq !== null && last.seq <= readSeq) return 'read'
  if (last.seq <= deliveredSeq) return 'delivered'
  return 'sent'
}
